import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/lib/currencyFormatter";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, Tag, Package } from "lucide-react";
import { MenuItem } from "@/types/menu";

interface PublicMenuItemCardProps {
  item: MenuItem;
  onClick?: (item: MenuItem) => void;
  onViewDetails: (item: MenuItem) => void;
  onAddToCart: (item: MenuItem, quantity: number) => void;
  showTags?: boolean;
}

export function PublicMenuItemCard({
  item,
  onClick,
  onAddToCart,
  onViewDetails,
  showTags = false,
}: PublicMenuItemCardProps) {
  // Handle null or empty description
  const displayDescription = item.description
    ? item.description.length > 100
      ? `${item.description.substring(0, 100)}...`
      : item.description
    : "";

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the card click
    if (item.available) {
      onAddToCart(item, 1);
    }
  };

  // Convert price to a number for formatting
  const priceAsNumber =
    typeof item.price === "string" ? parseFloat(item.price) : item.price;

  // Check if inventory information is available
  const hasInventoryInfo =
    item.always_available !== undefined ||
    typeof item.available_quantity === "number";

  const isLimitedStock =
    typeof item.available_quantity === "number" &&
    item.available_quantity > 0 &&
    item.available_quantity < 10 &&
    !item.always_available;

  return (
    <Card
      className={`h-full cursor-pointer transition-shadow hover:shadow-md ${
        !item.available ? "opacity-70" : ""
      }`}
      onClick={() => onViewDetails(item)}
    >
      <div className="relative">
        {item.image_url ? (
          <div className="aspect-[4/3] w-full overflow-hidden rounded-t-lg">
            <img
              src={item.image_url}
              alt={item.name}
              className="object-cover w-full h-full"
            />
          </div>
        ) : (
          <div className="aspect-[4/3] w-full bg-muted rounded-t-lg flex items-center justify-center">
            <span className="text-muted-foreground">No image</span>
          </div>
        )}

        {/* Show inventory status badges */}
        {hasInventoryInfo && (
          <div className="absolute top-2 left-2 flex flex-col gap-1">
            {item.always_available && (
              <Badge
                variant="secondary"
                className="text-xs bg-green-100 text-green-800 border border-green-300"
              >
                Always Available
              </Badge>
            )}

            {isLimitedStock && (
              <Badge
                variant="secondary"
                className="text-xs bg-amber-100 text-amber-800 border border-amber-300"
              >
                Only {item.available_quantity} left
              </Badge>
            )}
          </div>
        )}

        {!item.available && (
          <div className="absolute inset-0 bg-background/80 flex items-center justify-center rounded-t-lg">
            <Badge variant="destructive" className="text-xs px-2 py-1">
              Unavailable
            </Badge>
          </div>
        )}
      </div>

      <CardContent className="p-4">
        <div className="mb-2 flex items-start justify-between">
          <h3 className="font-medium line-clamp-1 mr-2">{item.name}</h3>
          <span className="font-semibold shrink-0">
            {formatCurrency(priceAsNumber)}
          </span>
        </div>

        {displayDescription && (
          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
            {displayDescription}
          </p>
        )}

        <div className="flex justify-between items-center">
          {showTags && item.tags && item.tags.length > 0 && (
            <div className="flex items-center space-x-1 text-xs text-muted-foreground">
              <Tag className="h-3 w-3" />
              <span className="line-clamp-1">
                {item.tags.slice(0, 2).join(", ")}
                {item.tags.length > 2 ? "..." : ""}
              </span>
            </div>
          )}

          <Button
            variant="secondary"
            size="sm"
            className="ml-auto"
            onClick={handleAddToCart}
            disabled={!item.available}
          >
            <ShoppingCart className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
